import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { useEssDashboard, useEssOvertimeMyRequests, useEssOvertimeSubmit } from "../../services/essService";

const formatCellDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(v);
  }
};

const otStatusColor = (s) => {
  const x = String(s || "").toLowerCase();
  if (x === "approved") return "green";
  if (x === "rejected") return "red";
  if (x === "pending") return "yellow";
  return "gray";
};

const EssOvertime = () => {
  const { essDashboardIsLoading, portal } = useEssDashboard();
  const canRequestOvertime = portal?.actions?.can_request_overtime !== false;
  const { overtimeRequests, overtimeRequestsIsLoading, refetchOvertimeRequests } =
    useEssOvertimeMyRequests();
  const { overtimeSubmit, overtimeSubmitIsLoading } = useEssOvertimeSubmit();

  const [date, setDate] = useState("");
  const [hours, setHours] = useState("0");
  const [reason, setReason] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canRequestOvertime) return;
    const h = parseFloat(hours);
    if (!date || Number.isNaN(h)) return;
    overtimeSubmit({
      date,
      hours_requested: h,
      reason: reason.trim(),
    });
  };

  return (
    <VStack align="stretch" spacing={8}>
      <Heading size="lg">Overtime</Heading>

      {!essDashboardIsLoading && !canRequestOvertime && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Overtime requests aren’t enabled for you in the portal right now. Contact HR.
          </Text>
        </Alert>
      )}

      <Card shadow="sm" border="1px solid" borderColor="gray.200">
        <CardHeader>
          <Heading size="md">Submit request</Heading>
        </CardHeader>
        <CardBody pt={0}>
          <Box as="form" onSubmit={handleSubmit} maxW="lg">
            <FormControl isRequired>
              <FormLabel>Date</FormLabel>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                borderRadius="md"
              />
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel>Hours</FormLabel>
              <NumberInput
                value={hours}
                onChange={setHours}
                min={0.5}
                max={24}
                step={0.5}
                precision={2}
              >
                <NumberInputField borderRadius="md" />
              </NumberInput>
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Reason</FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is overtime needed?"
                borderRadius="md"
                rows={3}
              />
            </FormControl>
            <Button
              type="submit"
              colorScheme="blue"
              mt={6}
              isLoading={overtimeSubmitIsLoading}
              isDisabled={!canRequestOvertime || !date}
            >
              Submit
            </Button>
          </Box>
        </CardBody>
      </Card>

      <Card shadow="sm" border="1px solid" borderColor="gray.200">
        <CardHeader>
          <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
            <Heading size="md">My requests</Heading>
            <Button size="xs" variant="ghost" onClick={() => refetchOvertimeRequests()}>
              Refresh
            </Button>
          </HStack>
        </CardHeader>
        <CardBody pt={0}>
          {overtimeRequestsIsLoading ? (
            <Text fontSize="sm" color="gray.600">
              Loading…
            </Text>
          ) : overtimeRequests.length === 0 ? (
            <Text fontSize="sm" color="gray.600">
              No overtime requests yet.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Hours</Th>
                    <Th>Status</Th>
                    <Th>Reason</Th>
                    <Th>Submitted</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {overtimeRequests.map((row) => (
                    <Tr key={row.request_id}>
                      <Td>{row.date}</Td>
                      <Td>{row.hours_requested}</Td>
                      <Td>
                        <Badge colorScheme={otStatusColor(row.status)}>{row.status}</Badge>
                      </Td>
                      <Td fontSize="sm" maxW="180px" noOfLines={2}>
                        {row.reason || "—"}
                      </Td>
                      <Td fontSize="sm">{formatCellDate(row.created_at)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </VStack>
  );
};

export default EssOvertime;
